import { WirelessStats } from "../../../apis/networkdashboard/rest/models";
import { COLOR_BLUE, COLOR_ORANGE, COLOR_RED } from "../../common/line-chart/colors";
import { LineChartLines } from "../../common/line-chart/line-chart.component";
import { toLineChartLine } from "../../common/line-chart/line-chart.helpers";

export const wirelessLineColors = [COLOR_BLUE, COLOR_RED, COLOR_ORANGE];

export type WirelessBand = "twofour" | "five" | "six";

export const WIRELESS_BANDS: WirelessBand[] = ["twofour", "five", "six"];

/** Band to its tooltip translation key. Written out so the keys stay searchable and the i18n linter sees them. */
const BAND_LABEL_KEYS: Record<WirelessBand, string> = {
  twofour: "Chart.TrafficGraph.Tooltip.Wireless.twofour",
  five: "Chart.TrafficGraph.Tooltip.Wireless.five",
  six: "Chart.TrafficGraph.Tooltip.Wireless.six",
};

export function bandLabelKey(band: WirelessBand): string {
  return BAND_LABEL_KEYS[band];
}

export const NUMBER_OF_USERS_KPI_KEY = "clients";

export interface KpiSubject {
  key: string;
  label: string;
}

export function buildKpiSubjects(chartChoices: KpiSubject[], sensorPresent: boolean): KpiSubject[] {
  const numberOfUsers = { key: NUMBER_OF_USERS_KPI_KEY, label: "Chart.Label.NumberOfUsersAllBands" };
  return sensorPresent ? [numberOfUsers, ...chartChoices] : [numberOfUsers];
}

function bandHasData(stats: WirelessStats, band: WirelessBand): boolean {
  return stats[band]?.some(([, value]) => value != null && value !== 0) ?? false;
}

function bandLabel(band: WirelessBand, translate: (key: string) => string): string {
  return translate(bandLabelKey(band));
}

export function buildAllBandsLabel(supportedBands: WirelessBand[], translate: (key: string) => string): string {
  const labels = supportedBands.map((band) => bandLabel(band, translate));
  if (labels.length === 0) return "";
  if (labels.length === 1) return labels[0];
  return `${labels.slice(0, -1).join(", ")} & ${labels[labels.length - 1]}`;
}

function buildAllBandsSeries(stats: WirelessStats): LineChartLines {
  const bandWithData = WIRELESS_BANDS.find((band) => bandHasData(stats, band));
  if (!bandWithData) return {};
  return { [NUMBER_OF_USERS_KPI_KEY]: toLineChartLine(stats[bandWithData] ?? []) };
}

function buildPerBandSeries(stats: WirelessStats): LineChartLines {
  const bandsWithData = WIRELESS_BANDS.filter((band) => bandHasData(stats, band));
  const lines: LineChartLines = {};
  bandsWithData.forEach((band) => {
    lines[band] = toLineChartLine(stats[band] ?? []);
  });
  return lines;
}

export function wirelessStatsToChartData(stats: WirelessStats, selectedKpi: string): LineChartLines {
  return selectedKpi === NUMBER_OF_USERS_KPI_KEY ? buildAllBandsSeries(stats) : buildPerBandSeries(stats);
}
